// Line check photos on the operator screens (migration 034): reference photos
// beside a check, "Add a photo" on a bad answer, the photos already sent, and
// Reference | What was seen for the verifier.
//
// The operator look, not shadcn: 56px gloved targets, one radius, system font.
// A photo is optional, always, and never a judgement (plan 2026-10-28).
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useQueue } from '../../offline/useQueue';
import { opFocus } from '../../shells/OperatorShell';
import { PhotoPicker } from './PhotoPicker';
import { type DraftPhoto } from './receiveModel';
import {
  MAX_LINE_PHOTOS,
  readingPhotoPath,
  referencePhotoBlob,
  type ReadingPhoto,
  type ReferencePhoto,
  type RefSource,
} from './linePhotoModel';

const canMakeUrl = () => typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';

/** An object URL for bytes loaded with the bearer token; revoked when it goes. */
function usePhotoUrl(load: (signal: AbortSignal) => Promise<Blob>, key: string) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const ctl = new AbortController();
    let made: string | null = null;
    setFailed(false);
    load(ctl.signal)
      .then((blob) => {
        if (ctl.signal.aborted) return;
        if (canMakeUrl()) {
          made = URL.createObjectURL(blob);
          setUrl(made);
        }
      })
      .catch(() => {
        if (!ctl.signal.aborted) setFailed(true);
      });
    return () => {
      ctl.abort();
      if (made) URL.revokeObjectURL(made);
      setUrl(null);
    };
  }, [key, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { url, failed, retry };
}

/** Full screen. Tap anywhere or press Escape to close. */
export function PhotoViewer({ url, alt, caption, onClose }: { url: string; alt: string; caption?: string | null; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={alt} className="fixed inset-0 z-50 bg-black/90">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close photo"
        className={`flex h-full w-full flex-col items-center justify-center gap-(--s3) p-(--s4) ${opFocus}`}
      >
        <img src={url} alt={alt} className="max-h-[85dvh] max-w-full rounded-(--radius) object-contain" />
        <span className="text-(length:--fs-md) font-semibold text-white">{caption ? `${caption} · ` : ''}Tap to close</span>
      </button>
    </div>
  );
}

/** One thumbnail, 56px, tap to enlarge. A failed read offers Try again (rule 15). */
function Thumb({
  load,
  loadKey,
  alt,
  caption,
  size = 56,
}: {
  load: (signal: AbortSignal) => Promise<Blob>;
  loadKey: string;
  alt: string;
  caption?: string | null;
  size?: 56 | 120;
}) {
  const { url, failed, retry } = usePhotoUrl(load, loadKey);
  const [open, setOpen] = useState(false);
  const box = size === 56 ? 'h-[56px] w-[56px]' : 'h-[120px] w-[120px]';
  if (failed) {
    return (
      <button
        type="button"
        onClick={retry}
        aria-label={`${alt} could not be loaded. Try again`}
        className={`${box} shrink-0 rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) text-(length:--fs-xs) font-semibold text-(--warn) ${opFocus}`}
      >
        Try again
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => url && setOpen(true)}
        aria-label={`Show ${alt}`}
        className={`${box} shrink-0 overflow-hidden rounded-(--radius) border border-(--border) bg-(--subtle) ${opFocus}`}
      >
        {url ? <img src={url} alt={alt} className="h-full w-full object-cover" /> : null}
      </button>
      {open && url ? <PhotoViewer url={url} alt={alt} caption={caption} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** Reference photos beside a check. Nothing when there are none or the list is not known. */
export function ReferenceThumbs({
  specParameterId,
  photos,
  source,
  label,
  size = 56,
}: {
  specParameterId: number;
  photos: ReadonlyArray<ReferencePhoto> | null | undefined;
  source: RefSource;
  label: string;
  size?: 56 | 120;
}) {
  if (!photos || photos.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-wrap gap-(--s2) p-0" aria-label={`Reference photos for ${label}`}>
      {photos.map((p, i) => (
        <li key={p.photoId}>
          <Thumb
            load={(signal) => referencePhotoBlob(specParameterId, p, source, signal)}
            loadKey={`ref:${specParameterId}:${p.photoId}:${p.sha256}`}
            alt={p.caption ? `reference: ${p.caption}` : `reference photo ${i + 1} for ${label}`}
            caption={p.caption}
            size={size}
          />
        </li>
      ))}
    </ul>
  );
}

/** Photos the server has on a reading, read fresh (never cached: they are a record). */
export function SeenThumbs({
  readingId,
  photos,
  label,
  size = 56,
}: {
  readingId: number | string;
  photos: ReadonlyArray<ReadingPhoto>;
  label: string;
  size?: 56 | 120;
}) {
  if (photos.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-wrap gap-(--s2) p-0" aria-label={`Photos taken of ${label}`}>
      {photos.map((p, i) => (
        <li key={p.photoId}>
          <Thumb
            load={(signal) => api.blob(readingPhotoPath(readingId, p.photoId), signal)}
            loadKey={`seen:${readingId}:${p.photoId}`}
            alt={`photo ${i + 1} of ${label}`}
            caption={[p.uploadedByUpn, p.capturedAtDevice ? new Date(p.capturedAtDevice).toLocaleString() : null]
              .filter(Boolean)
              .join(', ')}
            size={size}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * "Add a photo", at most four per check (the server's cap). PhotoPicker counts
 * against the dock's ten, so `total` is offset to leave exactly the room this
 * check has; at the cap the picker goes and one sentence says why.
 */
export function LinePhotoPicker({
  label,
  photos,
  alreadySent = 0,
  onAdd,
  onRemove,
}: {
  label: string;
  photos: readonly DraftPhoto[];
  alreadySent?: number;
  onAdd: (photos: Array<Pick<DraftPhoto, 'key' | 'blob' | 'capturedAtDevice'>>) => void;
  onRemove: (key: string) => void;
}) {
  const used = photos.length + alreadySent;
  return (
    <div className="mt-(--s3) flex flex-col gap-(--s2)">
      <span className="text-(length:--fs-sm) font-semibold">Add a photo (optional)</span>
      {used >= MAX_LINE_PHOTOS && photos.length === 0 ? null : (
        <PhotoPicker
          label={label}
          photos={photos}
          total={used}
          max={MAX_LINE_PHOTOS}
          per="check"
          onAdd={onAdd}
          onRemove={onRemove}
        />
      )}
      {used >= MAX_LINE_PHOTOS ? (
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">{MAX_LINE_PHOTOS} photos is the most one check keeps.</p>
      ) : null}
    </div>
  );
}

/** The verifier's view: Reference | What was seen, one column when only one exists, nothing when none. */
export function SideBySide({
  specParameterId,
  label,
  reference,
  readingId,
  seen,
}: {
  specParameterId: number;
  label: string;
  reference: ReadonlyArray<ReferencePhoto> | null | undefined;
  readingId: number | string | null;
  seen: ReadonlyArray<ReadingPhoto> | null | undefined;
}) {
  const hasRef = (reference?.length ?? 0) > 0;
  const hasSeen = readingId !== null && (seen?.length ?? 0) > 0;
  // Rule 15: an answered check whose photo list the server did not send is "not known", never "none".
  const seenUnknown = readingId !== null && seen == null;
  if (!hasRef && !hasSeen) {
    return seenUnknown ? <p className="m-0 mt-(--s2) text-(length:--fs-sm) text-(--muted)">Photos: not known.</p> : null;
  }
  return (
    <div className={`mt-(--s3) grid gap-(--s3) ${hasRef && hasSeen ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {hasRef ? (
        <div className="min-w-0">
          <h3 className="m-0 mb-(--s1) text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2)">Reference</h3>
          <ReferenceThumbs specParameterId={specParameterId} photos={reference} source="readiness" label={label} size={120} />
          {reference!.some((r) => r.caption) ? (
            <p className="m-0 mt-(--s1) text-(length:--fs-sm) text-(--muted)">
              {reference!.map((r) => r.caption).filter(Boolean).join(' · ')}
            </p>
          ) : null}
        </div>
      ) : null}
      {hasSeen ? (
        <div className="min-w-0">
          <h3 className="m-0 mb-(--s1) text-(length:--fs-xs) font-bold uppercase tracking-[0.06em] text-(--muted-2)">What was seen</h3>
          <SeenThumbs readingId={readingId!} photos={seen!} label={label} size={120} />
          <p className="m-0 mt-(--s1) wrap-break-word text-(length:--fs-sm) text-(--muted)">
            {seen!
              .map((p) => [p.uploadedByUpn, p.capturedAtDevice ? new Date(p.capturedAtDevice).toLocaleString() : null].filter(Boolean).join(', '))
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Photos the server did not take: Send again / Discard, by their owner only. */
export function PhotoRefusedBanner() {
  const { photosRefused, retryPhoto, discardPhoto } = useQueue();
  const [problem, setProblem] = useState<string | null>(null);
  if (!photosRefused || photosRefused.length === 0) return null;
  const act = (fn: () => Promise<void>) => {
    setProblem(null);
    fn().catch((e: unknown) => setProblem(e instanceof Error ? e.message : 'That did not work.'));
  };
  return (
    <div role="alert" className="flex-none border-b border-(--warn-line) bg-(--warn-bg) px-(--s4) py-(--s2)">
      <p className="m-0 text-(length:--fs-md) font-semibold text-(--warn)">
        {photosRefused.length === 1 ? 'A photo was not sent.' : `${photosRefused.length} photos were not sent.`} The readings are
        safe.
      </p>
      <ul className="m-0 list-none p-0">
        {photosRefused.map((p) => (
          <li key={p.photoKey} className="mt-(--s2) flex flex-wrap items-center gap-(--s2)">
            <span className="min-w-0 flex-1 text-(length:--fs-sm)">
              {p.parameterName ? `${p.parameterName}: ` : ''}
              {p.message}
            </span>
            <button
              type="button"
              onClick={() => act(() => retryPhoto(p.photoKey))}
              className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) font-semibold ${opFocus}`}
            >
              Send again
            </button>
            <button
              type="button"
              onClick={() => act(() => discardPhoto(p.photoKey))}
              className={`min-h-[56px] rounded-(--radius) border border-(--border-strong) bg-(--card) px-(--s3) text-(length:--fs-sm) font-semibold ${opFocus}`}
            >
              Discard
            </button>
          </li>
        ))}
      </ul>
      {problem ? <p className="m-0 mt-(--s2) text-(length:--fs-sm) font-semibold text-(--warn)">{problem}</p> : null}
    </div>
  );
}
